import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import {
  Check,
  PackagePlus,
  Plus,
  RefreshCcw,
  Send,
  ShieldCheck,
  Store,
  Wrench,
} from 'lucide-react';

import { useAgents } from '../contexts/AgentsContext';
import { useNotification } from '../hooks/useNotification';
import {
  bindMarketplaceItemToAgent,
  browseMarketplacePublic,
  createMarketplaceDomainVerification,
  createMarketplaceItem,
  createMarketplaceVersion,
  installMarketplaceItem,
  listMarketplaceDomainVerifications,
  listMarketplaceInstallations,
  listMarketplaceItems,
  MarketplaceDomainVerification,
  MarketplaceInstallation,
  MarketplaceItem,
  MarketplaceItemType,
  submitExecutionReview,
  submitPublicReview,
  updateMarketplaceInstallation,
  verifyMarketplaceDomainVerification,
} from '../services/marketplace.service';

const defaultSkillPayload = {
  name: 'support_triage',
  title: 'Support Triage',
  short_description: 'Classify support requests and suggest next steps.',
  instruction_content: 'Classify the request by urgency and return a concise recommendation.',
  risk_tags: [],
};

const defaultToolPayload = {
  name: 'lookup_order',
  title: 'Lookup Order',
  description: 'Read order status from an external service.',
  input_schema: {
    type: 'object',
    properties: { order_id: { type: 'string' } },
    required: ['order_id'],
  },
  endpoint_url: 'https://api.example.com/tools/orders/lookup',
  method: 'POST',
  auth_type: 'hmac_sha256',
  auth_secret_ref: 'secret:orders/signing-key',
  risk_level: 'read',
  tool_annotations: { readOnlyHint: true, openWorldHint: true },
  requires_confirmation: false,
};

const defaultMcpPayload = {
  server_label: 'docs',
  display_name: 'Docs MCP',
  description: 'Search documentation through an approved MCP server.',
  server_url: 'https://mcp.example.com',
  transport: 'streamable_http',
  auth_config: { oauth_connection_ref: 'connection:docs/oauth' },
  config_scope: 'user',
  tool_snapshot: [
    {
      toolName: 'search_docs',
      description: 'Search documentation.',
      inputSchema: { type: 'object', properties: { q: { type: 'string' } }, required: ['q'] },
      annotations: { readOnlyHint: true, openWorldHint: true },
    },
  ],
};

const payloadByType: Record<MarketplaceItemType, Record<string, unknown>> = {
  skill: defaultSkillPayload,
  tool: defaultToolPayload,
  mcp_server: defaultMcpPayload,
};

const statusColor = (status: string) => {
  if (status.includes('approved') || status === 'verified') return 'success';
  if (status.includes('pending') || status.includes('reviewed')) return 'warning';
  if (
    status.includes('rejected') ||
    status.includes('suspended') ||
    status === 'disabled' ||
    status === 'failed' ||
    status === 'expired'
  ) {
    return 'error';
  }
  return 'default';
};

export const MarketplacePage: React.FC = () => {
  const notification = useNotification();
  const { agents } = useAgents();
  const [tab, setTab] = useState<'mine' | 'public'>('mine');
  const [items, setItems] = useState<MarketplaceItem[]>([]);
  const [publicItems, setPublicItems] = useState<MarketplaceItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [type, setType] = useState<MarketplaceItemType>('skill');
  const [title, setTitle] = useState('Support Triage');
  const [name, setName] = useState('support_triage');
  const [payloadText, setPayloadText] = useState(JSON.stringify(defaultSkillPayload, null, 2));
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [installations, setInstallations] = useState<Record<string, MarketplaceInstallation>>({});
  const [domainVerifications, setDomainVerifications] = useState<MarketplaceDomainVerification[]>(
    []
  );
  const [domain, setDomain] = useState('');
  const [domainMethod, setDomainMethod] = useState<'http_well_known' | 'dns_txt'>(
    'http_well_known'
  );

  const bindableAgents = useMemo(
    () => agents.filter((agent) => agent.isActive !== false && agent.status !== 'archived'),
    [agents]
  );

  useEffect(() => {
    void refresh();
  }, []);

  useEffect(() => {
    const payload = payloadByType[type];
    setPayloadText(JSON.stringify(payload, null, 2));
    setName(String(payload.name || payload.server_label));
    setTitle(String(payload.title || payload.display_name));
  }, [type]);

  useEffect(() => {
    if (!selectedAgentId && bindableAgents[0]?.publicId) {
      setSelectedAgentId(bindableAgents[0].publicId);
    }
  }, [bindableAgents, selectedAgentId]);

  const refresh = async () => {
    setLoading(true);
    try {
      const [mine, listings, installed, domains] = await Promise.all([
        listMarketplaceItems(),
        browseMarketplacePublic(),
        listMarketplaceInstallations(),
        listMarketplaceDomainVerifications(),
      ]);
      setItems(mine.data ?? []);
      setPublicItems(listings.data ?? []);
      setInstallations(
        Object.fromEntries(
          (installed.data ?? []).map((installation) => [installation.itemPublicId, installation])
        )
      );
      setDomainVerifications(domains.data ?? []);
    } catch (error) {
      notification.error(error instanceof Error ? error.message : 'Failed to load marketplace');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDomainVerification = async () => {
    if (!domain.trim()) {
      notification.error('Domain is required');
      return;
    }
    try {
      const response = await createMarketplaceDomainVerification({
        domain: domain.trim(),
        method: domainMethod,
      });
      if (response.data) {
        setDomainVerifications((current) => [response.data!, ...current]);
        setDomain('');
      }
      notification.success('Domain verification created');
    } catch (error) {
      notification.error(
        error instanceof Error ? error.message : 'Failed to create domain verification'
      );
    }
  };

  const handleVerifyDomain = async (verification: MarketplaceDomainVerification) => {
    try {
      const response = await verifyMarketplaceDomainVerification(verification.publicId);
      if (response.data) {
        setDomainVerifications((current) =>
          current.map((item) => (item.publicId === verification.publicId ? response.data! : item))
        );
      }
      notification.success('Domain verification checked');
    } catch (error) {
      notification.error(error instanceof Error ? error.message : 'Failed to verify domain');
    }
  };

  const handleCreate = async () => {
    let payload: Record<string, unknown>;
    try {
      payload = JSON.parse(payloadText) as Record<string, unknown>;
    } catch {
      notification.error('Payload must be valid JSON');
      return;
    }

    try {
      const itemResponse = await createMarketplaceItem({
        type,
        name,
        title,
        description: String(payload.description || payload.short_description || ''),
        visibility: 'private',
      });
      if (!itemResponse.data) throw new Error(itemResponse.error || 'Item create failed');
      const versionResponse = await createMarketplaceVersion(itemResponse.data.publicId, {
        payload,
      });
      if (!versionResponse.data) throw new Error(versionResponse.error || 'Version create failed');
      await submitExecutionReview(versionResponse.data.publicId);
      notification.success('Marketplace item submitted for execution review');
      setCreateOpen(false);
      await refresh();
    } catch (error) {
      notification.error(
        error instanceof Error ? error.message : 'Failed to create marketplace item'
      );
    }
  };

  const handleSubmitPublic = async (item: MarketplaceItem) => {
    try {
      await submitPublicReview(item.publicId, item.activeExecutionVersion?.publicId ?? undefined);
      notification.success('Submitted for public marketplace review');
      await refresh();
    } catch (error) {
      notification.error(error instanceof Error ? error.message : 'Failed to submit public review');
    }
  };

  const handleInstall = async (item: MarketplaceItem) => {
    try {
      const response = await installMarketplaceItem(item.publicId);
      if (response.data) {
        setInstallations((current) => ({ ...current, [item.publicId]: response.data! }));
      }
      notification.success('Marketplace item installed');
    } catch (error) {
      notification.error(error instanceof Error ? error.message : 'Failed to install item');
    }
  };

  const handleUpdateInstallation = async (
    item: MarketplaceItem,
    installation: MarketplaceInstallation
  ) => {
    const versionPublicId = item.activePublicVersion?.publicId;
    if (!versionPublicId) {
      notification.error('No approved public version is available');
      return;
    }
    try {
      const response = await updateMarketplaceInstallation(installation.publicId, versionPublicId);
      if (response.data) {
        setInstallations((current) => ({ ...current, [item.publicId]: response.data! }));
      }
      notification.success('Installed marketplace item updated');
    } catch (error) {
      notification.error(error instanceof Error ? error.message : 'Failed to update installation');
    }
  };

  const handleBind = async (item: MarketplaceItem) => {
    if (!selectedAgentId) {
      notification.error('Select an agent first');
      return;
    }
    try {
      await bindMarketplaceItemToAgent({
        agentPublicId: selectedAgentId,
        sourceType: 'owner_item',
        itemPublicId: item.publicId,
      });
      notification.success('Marketplace item bound to agent');
    } catch (error) {
      notification.error(error instanceof Error ? error.message : 'Failed to bind item');
    }
  };

  const handleBindInstallation = async (installation: MarketplaceInstallation) => {
    if (!selectedAgentId) {
      notification.error('Select an agent first');
      return;
    }
    try {
      await bindMarketplaceItemToAgent({
        agentPublicId: selectedAgentId,
        sourceType: 'public_installation',
        installationPublicId: installation.publicId,
      });
      notification.success('Installed marketplace item bound to agent');
    } catch (error) {
      notification.error(error instanceof Error ? error.message : 'Failed to bind installed item');
    }
  };

  return (
    <Box className="ambient-surface min-h-screen bg-slate-50 p-4 dark:bg-slate-900 md:p-6">
      <Box className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <Box>
          <Typography variant="h5" className="font-bold text-gray-900 dark:text-slate-100">
            Skills & Tools
          </Typography>
          <Typography variant="body2" className="text-gray-600 dark:text-slate-400">
            Manage reviewed skills, webhook tools, MCP servers, and public installs.
          </Typography>
        </Box>
        <Box className="flex gap-2">
          <Button
            variant="outlined"
            startIcon={loading ? <CircularProgress size={16} /> : <RefreshCcw size={16} />}
            onClick={() => void refresh()}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<Plus size={16} />}
            onClick={() => setCreateOpen(true)}
          >
            Create
          </Button>
        </Box>
      </Box>

      <Paper className="overflow-hidden border border-gray-200 dark:border-slate-700 dark:bg-slate-800">
        <Box className="border-b border-gray-200 px-4 dark:border-slate-700">
          <Tabs value={tab} onChange={(_, value) => setTab(value)}>
            <Tab value="mine" label="My items" />
            <Tab value="public" label="Public marketplace" />
          </Tabs>
        </Box>

        <Box className="p-4">
          {tab === 'mine' && (
            <Box className="space-y-3">
              {items.length === 0 && <Alert severity="info">No marketplace items yet.</Alert>}
              {items.map((item) => (
                <Paper
                  key={item.publicId}
                  variant="outlined"
                  className="p-4 dark:border-slate-700 dark:bg-slate-900"
                >
                  <Box className="flex flex-wrap items-start justify-between gap-3">
                    <Box className="min-w-0">
                      <Box className="mb-2 flex flex-wrap items-center gap-2">
                        <Wrench size={18} />
                        <Typography variant="subtitle1" className="font-semibold">
                          {item.title}
                        </Typography>
                        <Chip size="small" label={item.type} />
                        <Chip
                          size="small"
                          color={statusColor(item.executionStatus)}
                          label={item.executionStatus}
                        />
                        <Chip
                          size="small"
                          color={statusColor(item.publicationStatus)}
                          label={item.publicationStatus}
                        />
                      </Box>
                      <Typography variant="body2" className="text-gray-600 dark:text-slate-400">
                        {item.description || item.name}
                      </Typography>
                    </Box>
                    <Box className="flex flex-wrap gap-2">
                      <Button
                        size="small"
                        startIcon={<Check size={16} />}
                        disabled={item.executionStatus !== 'approved_for_execution'}
                        onClick={() => void handleBind(item)}
                      >
                        Bind
                      </Button>
                      <Button
                        size="small"
                        startIcon={<Send size={16} />}
                        disabled={item.executionStatus !== 'approved_for_execution'}
                        onClick={() => void handleSubmitPublic(item)}
                      >
                        Submit public
                      </Button>
                    </Box>
                  </Box>
                </Paper>
              ))}
            </Box>
          )}

          {tab === 'public' && (
            <Box className="space-y-3">
              {publicItems.length === 0 && (
                <Alert severity="info">No public marketplace listings.</Alert>
              )}
              {publicItems.map((item) => {
                const installation = installations[item.publicId];
                const hasUpdate =
                  Boolean(installation) &&
                  Boolean(item.activePublicVersion?.publicId) &&
                  installation.installedVersionPublicId !== item.activePublicVersion?.publicId;
                return (
                  <Paper
                    key={item.publicId}
                    variant="outlined"
                    className="p-4 dark:border-slate-700 dark:bg-slate-900"
                  >
                    <Box className="flex flex-wrap items-start justify-between gap-3">
                      <Box className="min-w-0">
                        <Box className="mb-2 flex flex-wrap items-center gap-2">
                          <Store size={18} />
                          <Typography variant="subtitle1" className="font-semibold">
                            {item.title}
                          </Typography>
                          <Chip size="small" label={item.type} />
                          <Chip size="small" label={`${item.installCount} installs`} />
                          {installation && (
                            <Chip
                              size="small"
                              color={hasUpdate ? 'warning' : 'success'}
                              label={hasUpdate ? 'Update available' : 'Installed'}
                            />
                          )}
                        </Box>
                        <Typography variant="body2" className="text-gray-600 dark:text-slate-400">
                          {item.description || item.name}
                        </Typography>
                      </Box>
                      <Box className="flex flex-wrap gap-2">
                        {!installation && (
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<PackagePlus size={16} />}
                            onClick={() => void handleInstall(item)}
                          >
                            Install
                          </Button>
                        )}
                        {installation && hasUpdate && (
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<RefreshCcw size={16} />}
                            onClick={() => void handleUpdateInstallation(item, installation)}
                          >
                            Update
                          </Button>
                        )}
                        {installation && !hasUpdate && (
                          <Button size="small" disabled startIcon={<Check size={16} />}>
                            Installed
                          </Button>
                        )}
                        {installation && (
                          <Button
                            size="small"
                            startIcon={<Check size={16} />}
                            onClick={() => void handleBindInstallation(installation)}
                          >
                            Bind
                          </Button>
                        )}
                      </Box>
                    </Box>
                  </Paper>
                );
              })}
            </Box>
          )}
        </Box>
      </Paper>

      <Box className="mt-4 max-w-xl">
        <FormControl fullWidth size="small">
          <InputLabel>Agent for bindings</InputLabel>
          <Select
            label="Agent for bindings"
            value={selectedAgentId}
            onChange={(event) => setSelectedAgentId(event.target.value)}
          >
            {bindableAgents.map((agent) => (
              <MenuItem key={agent.publicId} value={agent.publicId}>
                {agent.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Paper className="mt-4 border border-gray-200 p-4 dark:border-slate-700 dark:bg-slate-800">
        <Box className="mb-3 flex items-center gap-2">
          <ShieldCheck size={18} />
          <Typography variant="subtitle1" className="font-semibold">
            Domain verification
          </Typography>
        </Box>
        <Box className="grid gap-3 md:grid-cols-[1fr_220px_auto]">
          <TextField
            size="small"
            label="Domain"
            value={domain}
            onChange={(event) => setDomain(event.target.value)}
            placeholder="tools.example.com"
          />
          <FormControl size="small">
            <InputLabel>Method</InputLabel>
            <Select
              label="Method"
              value={domainMethod}
              onChange={(event) => setDomainMethod(event.target.value as typeof domainMethod)}
            >
              <MenuItem value="http_well_known">HTTP well-known</MenuItem>
              <MenuItem value="dns_txt">DNS TXT</MenuItem>
            </Select>
          </FormControl>
          <Button variant="contained" onClick={() => void handleCreateDomainVerification()}>
            Create
          </Button>
        </Box>
        <Box className="mt-3 space-y-2">
          {domainVerifications.length === 0 && (
            <Alert severity="info">No verified domains yet.</Alert>
          )}
          {domainVerifications.map((verification) => (
            <Paper
              key={verification.publicId}
              variant="outlined"
              className="p-3 dark:border-slate-700"
            >
              <Box className="flex flex-wrap items-center justify-between gap-3">
                <Box className="min-w-0">
                  <Box className="mb-1 flex flex-wrap items-center gap-2">
                    <Typography variant="body2" className="font-semibold">
                      {verification.domain}
                    </Typography>
                    <Chip size="small" label={verification.method} />
                    <Chip
                      size="small"
                      color={statusColor(verification.status)}
                      label={verification.status}
                    />
                  </Box>
                  {verification.instructions?.url && (
                    <Typography
                      variant="caption"
                      className="block break-all text-gray-600 dark:text-slate-400"
                    >
                      {verification.instructions.url}
                    </Typography>
                  )}
                  {verification.instructions?.name && (
                    <Typography
                      variant="caption"
                      className="block break-all text-gray-600 dark:text-slate-400"
                    >
                      {verification.instructions.name}
                    </Typography>
                  )}
                  {verification.verificationToken && (
                    <Typography
                      variant="caption"
                      className="block break-all font-mono text-gray-700 dark:text-slate-300"
                    >
                      {verification.verificationToken}
                    </Typography>
                  )}
                </Box>
                <Button
                  size="small"
                  startIcon={<ShieldCheck size={16} />}
                  onClick={() => void handleVerifyDomain(verification)}
                >
                  Verify
                </Button>
              </Box>
            </Paper>
          ))}
        </Box>
      </Paper>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Create marketplace item</DialogTitle>
        <DialogContent className="space-y-4">
          <Box className="grid gap-3 pt-2 md:grid-cols-3">
            <FormControl fullWidth size="small">
              <InputLabel>Type</InputLabel>
              <Select
                label="Type"
                value={type}
                onChange={(event) => setType(event.target.value as MarketplaceItemType)}
              >
                <MenuItem value="skill">Skill</MenuItem>
                <MenuItem value="tool">Webhook tool</MenuItem>
                <MenuItem value="mcp_server">MCP server</MenuItem>
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="Name"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
            <TextField
              size="small"
              label="Title"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
            />
          </Box>
          <TextField
            fullWidth
            multiline
            minRows={18}
            label="Payload JSON"
            value={payloadText}
            onChange={(event) => setPayloadText(event.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => void handleCreate()}>
            Create and submit
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
